@file:Suppress("DEPRECATION")
package org.example.newportfolio.components.sections.products.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.style.ComponentStyle
import org.jetbrains.compose.web.dom.Img
import com.varabyte.kobweb.silk.components.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.components.style.hover
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.example.newportfolio.models.Product
import org.example.newportfolio.theme.brand
import org.example.newportfolio.theme.fonts.BodyLargeTextStyle
import org.example.newportfolio.theme.fonts.TextStyle
import org.example.newportfolio.theme.fonts.TitleTextStyle
import org.jetbrains.compose.web.css.*

val ProductItemStyle by ComponentStyle {
    base {
        Modifier
            .position(Position.Relative)
            .borderRadius(30.px)
            .overflow(Overflow.Hidden)
            .cursor(Cursor.Pointer)
            .transition(CSSTransition("transform", 0.3.s, TransitionTimingFunction.EaseInOut))
            .width(100.percent)
    }
    hover {
        Modifier
            .transform { scale(1.05) }
            .boxShadow(0.px, 15.px, 35.px, 0.px, Color.rgba(52, 152, 219, 0.35f))
            .border(2.px, LineStyle.Solid, Color.rgba(52, 152, 219, 0.5f))
    }
    cssRule(":hover .product-item-shadow") {
        Modifier.fillMaxHeight()
    }
    cssRule(":hover .product-item-overlay") {
        Modifier.opacity(1).transform { translateY(0.px) }
    }
    cssRule(":hover .product-item-image") {
        Modifier.transform { scale(1.1) }
    }
    cssRule(".product-item-img") {
        Modifier
            .width(100.percent)
            .height(100.percent)
    }
}

val ProductItemShadowStyle by ComponentStyle {
    base {
        Modifier
            .position(Position.Absolute)
            .zIndex(1)
            .top(0.px)
            .left(0.px)
            .fillMaxWidth()
            .height(0.px)
            .backgroundColor(Color.rgb(r = 29, g = 28, b = 38))
            .opacity(0.85)
            .transition(CSSTransition("height", 0.4.s, TransitionTimingFunction.EaseInOut))
    }
}

val ProductItemImageContainerStyle by ComponentStyle {
    base {
        Modifier
            .width(100.percent)
            .height(400.px)
            .backgroundColor(Colors.White)
    }
    Breakpoint.MD { Modifier.height(460.px) }
    Breakpoint.LG { Modifier.height(500.px) }
    Breakpoint.XL { Modifier.height(560.px) }
}

val ProductItemOverlayStyle by ComponentStyle {
    base {
        Modifier
            .position(Position.Absolute)
            .zIndex(2)
            .textAlign(TextAlign.Center)
            .transition(CSSTransition("all", 0.5.s, TransitionTimingFunction.EaseInOut))
            .opacity(0)
            .fillMaxSize()
            .color(Colors.White)
            .transform { translateY(20.px) }
    }
}

@Composable
fun ProductItem(
    product: Product,
    modifier: Modifier = Modifier,
) {
    val ctx = rememberPageContext()
    val palette = ColorMode.current.toPalette()

    Box(
        modifier = ProductItemStyle.toModifier()
            .then(modifier)
            .onClick {
                ctx.router.navigateTo("/product/${product.name.lowercase()}")
            },
        contentAlignment = Alignment.Center
    ) {
        // White card as image container (user will provide screenshots later)
        Box(modifier = ProductItemImageContainerStyle.toModifier()) {
            Img(
                src = product.imageRes,
                attrs = Modifier.fillMaxSize()
                    .objectFit(ObjectFit.Contain)
                    .padding(20.px)
                    .classNames("product-item-img", "product-item-image")
                    .toAttrs {
                        attr("alt", "${product.productName} - Mobile App by Mohamed Elgohary")
                        attr("loading", "lazy")
                        attr("decoding", "async")
                    }
            )
        }
        Box(
            modifier = ProductItemShadowStyle.toModifier().classNames("product-item-shadow")
        )
        Column(
            modifier = ProductItemOverlayStyle.toModifier().classNames("product-item-overlay"),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            SpanText(
                text = product.productName,
                modifier = TextStyle.toModifier(TitleTextStyle)
            )
            SpanText(
                text = product.tagline,
                modifier = TextStyle.toModifier(BodyLargeTextStyle)
                    .margin(top = 0.5.cssRem)
                    .padding(leftRight = 20.px)
            )
            // Status pill
            Box(
                modifier = Modifier
                    .margin(top = 1.cssRem)
                    .padding(leftRight = 16.px, topBottom = 6.px)
                    .borderRadius(20.px)
                    .backgroundColor(palette.brand.primary.toRgb().copyf(alpha = 0.3f))
                    .border(1.px, LineStyle.Solid, palette.brand.primary.toRgb().copyf(alpha = 0.5f))
            ) {
                SpanText(
                    text = "View Details →",
                    modifier = Modifier.color(Colors.White).fontSize(13.px)
                )
            }
        }
    }
}
